<template>
  <div class="overflow-y-scroll h-full min-h-full pb-32">
    <div
      v-if="subView === 'Default'"
      class="flex-1 relative z-0"
    >
      <div class="bg-white shadow">
        <div class="px-4 sm:px-6 lg:mx-auto lg:px-8 max-w-7xl">
          <div class="pt-6 pb-6 md:flex md:items-center justify-between lg:border-t lg:border-cool-gray-200">
            <div class="text-3xl font-bold leading-7 text-cool-gray-900 sm:leading-9 sm:truncate">
              Courses
            </div>

            <div class="flex items-center">
              <PrimaryButton
                text="New course"
                icon="fal fa-plus"
                @click="handleNewCourseClick"
              />
              <div class="w-72 ml-2">
                <Dropdown
                  :list="dropdownOptions"
                  list-type-name="course"
                  :default-item="selectedCourse"
                  @select="selectCourse"
                />
                <div
                  v-if="selectedCourse && selectedCourse.id"
                  class="mt-2 flex items-center text-sm text-gray-600"
                >
                  <span class="mr-2">Course ID: <span class="font-mono font-semibold">{{ selectedCourse.id }}</span></span>
                  <button
                    type="button"
                    class="text-gray-500 hover:text-gray-700 transition-colors duration-200 focus:outline-none"
                    @click="copyCourseId"
                    title="Copy Course ID"
                  >
                    <i class="far fa-copy" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="pb-4">
            <Tabs
              :tabs="tabs"
              :selected-tab="selectedTab"
              @click="setTab"
            />
          </div>
        </div>
      </div>

      <TeeConfigForm
        v-if="selectedCourse && selectedCourse.id"
        class="-mt-20"
        :days="teeConfigDays || []"
        :tee-configs="teeConfigs || []"
        @success="newCourseSuccess"
      />
    </div>

    <div v-if="subView === 'NewCourse'">
      <CourseForm
        @success="newCourseSuccess"
        @back="secondaryClick"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Tabs from '@/app/shared/components/navigation/tabs/tabs.vue';
import Dropdown from '@/app/shared/components/form/dropdown/dropdown.vue';
import PrimaryButton from '@/app/shared/components/base/button/primaryButton.vue';
import { GetCoursesByClub } from '@/app/shared/services/courseService/courseService';
import TeeConfigForm from '@/app/shared/components/form/teeConfigForm.vue';
import { GetTeeConfigByCourseId, GetDaysByTeeConfigId } from '@/app/shared/services/teeConfigService/teeConfigService.js';
import {
  GetAllCourseConfigs, GetCourseConfig, GetCourseConfigById,
} from '@/app/shared/services/courseConfigService/courseConfigService';
import CourseForm from './courseForm.vue';

export default {
  name: 'Course',

  components: {
    Tabs,
    Dropdown,
    PrimaryButton,
    TeeConfigForm,
    CourseForm,
  },

  data() {
    return {
      tabs: [
        // { primaryText: 'Summary' },
        { primaryText: 'Configuration' },
      ],
      selectedTab: 'Configuration',
      subView: 'Default',

      configurations: [],
      teeConfigs: [],
      teeConfigDays: [],
    };
  },

  computed: {
    ...mapState({
      selectedClub: state => state.club.selectedClub,
      courses: state => state.course.availableCourses,
      selectedCourse: state => state.course.selectedCourse,
      tenantId: state => state.user.tenantId,
    }),

    dropdownOptions() {
      const options = [];

      if (this.courses !== [] && this.configurations !== []) {
        this.courses.forEach((element) => {
          const config = this.configurations.filter(c => c.courseId === element.id);
          if (config.length > 0) {
            const first = config[0];
            options.push({ id: element.id, primaryText: element.name, secondaryText: `${first.numberOfHoles} Holes` });
          }
        });
      }

      options.sort((a, b) => a.primaryText.localeCompare(b.primaryText));

      return options;
    },
  },

  watch: {
    courses() {
      this.getConfig();
    },
    selectedCourse() {
      if (this.selectedCourse && this.selectedCourse.id) {
        this.getTeeConfig();
      } else {
        this.teeConfigs = [];
        this.teeConfigDays = [];
      }
    },
    '$route'(to, from) {
      // Update subView based on route query parameter
      if (to.path === '/Courses') {
        if (to.query.NewCourse === 'true') {
          this.subView = 'NewCourse';
        } else {
          this.subView = 'Default';
        }
      }
    },
  },

  mounted() {
    // Reset to default view when component mounts
    this.subView = 'Default';

    const url_string = window.location.href;
    const url = new URL(url_string);

    const value = url.searchParams.get('NewCourse');

    // Only show form if explicitly requested via URL parameter
    if (value) {
      this.subView = 'NewCourse';
    } else {
      // Only call these if we have courses and a selected course
      if (this.courses.length > 0) {
        this.getConfig();
        if (this.selectedCourse && this.selectedCourse.id) {
          this.getTeeConfig();
        }
      }
    }
  },

  methods: {
    selectCourse(course) {
      const selectedCourse = this.courses.filter(c => c.id === course[0].id);
      this.$store.dispatch('setSelectedCourse', selectedCourse[0]);
    },

    getTeeConfig() {
      if (!this.selectedCourse || !this.selectedCourse.id) {
        this.teeConfigs = [];
        this.teeConfigDays = [];
        return;
      }
      this.teeConfigs = [];
      GetTeeConfigByCourseId(this.selectedCourse.id).then((data) => {
        if (!data || !data.length || !data[0]) {
          // No tee config exists - set empty arrays so form can be shown to create one
          this.teeConfigs = [];
          this.teeConfigDays = [];
          return;
        }
        const configData = data[0];
        this.teeConfigs.push(configData);
        if (configData && configData.id) {
          GetDaysByTeeConfigId(configData.id).then((days) => {
            console.log('[Courses] Fetched tee config days:', days);
            console.log('[Courses] Days array length:', days ? days.length : 0);
            this.teeConfigDays = days || [];
            // Force Vue to update
            this.$forceUpdate();
          }).catch((error) => {
            console.error('Error fetching tee config days:', error);
            this.teeConfigDays = [];
          });
        } else {
          this.teeConfigDays = [];
        }
      }).catch((error) => {
        console.error('Error fetching tee config:', error);
        // On error, still show form with empty arrays so user can create one
        this.teeConfigs = [];
        this.teeConfigDays = [];
      });
    },

    async getConfig() {
      this.configurations = [];
      // Wait for all course configurations to be loaded
      await Promise.all(this.courses.map(async (course) => {
        try {
          const data = await GetCourseConfigById(course.id);
          this.configurations.push(data);
        } catch (error) {
          // If config doesn't exist yet for a course, skip it
          console.log(`No config found for course ${course.id}`);
        }
      }));
    },

    setTab(tab) {
      this.selectedTab = tab;
    },

    async newCourseSuccess() {
      // Route will be changed by courseForm component, so subView will update via route watcher
      // Wait for courses to be reloaded
      await this.$store.dispatch('getAvailableCourses', this.selectedClub);
      // Reload configurations for all courses (needed for dropdown)
      await this.getConfig();
      // Force Vue to update the computed property
      this.$forceUpdate();
    },


    handleNewCourseClick() {
      // Change route to include NewCourse query parameter
      this.$router.push({ path: '/Courses', query: { NewCourse: 'true' } });
    },

    secondaryClick() {
      // Change route back to Courses without query parameter
      this.$router.push({ path: '/Courses' });
    },

    async copyCourseId() {
      if (!this.selectedCourse || !this.selectedCourse.id) {
        return;
      }

      try {
        await navigator.clipboard.writeText(this.selectedCourse.id);
        // Optional: Show a toast notification or feedback
        // You could add a toast library or simple alert here
        console.log('Course ID copied to clipboard');
      } catch (err) {
        console.error('Failed to copy Course ID:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = this.selectedCourse.id;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          console.log('Course ID copied to clipboard (fallback)');
        } catch (fallbackErr) {
          console.error('Fallback copy failed:', fallbackErr);
        }
        document.body.removeChild(textArea);
      }
    },
  },

};
</script>
